import { Image } from '@admin-panel/components';
import styled from '@emotion/styled';
import { TrashIcon } from '@phosphor-icons/react';
import { Button,Flex,Popconfirm } from 'antd';
import { useCallback,useMemo } from 'react';

import { createPortal } from 'react-dom';

export const ImageMaskWrapper = styled(Flex)`
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 4px;
  cursor: zoom-in;
`;

interface IImageMaskProps {
  fileUrl: string | undefined;
  onDelete?: VoidFunction;
  magnificationFactor: number;
  isPreviewOpen: boolean;
  setIsPreviewOpen: (open: boolean) => void;
  previewCoords: {
    left?: number;
    right?: number;
  };
}

const ImageMask = ({
  fileUrl,
  onDelete,
  magnificationFactor,
  isPreviewOpen,
  setIsPreviewOpen,
  previewCoords,
}: IImageMaskProps) => {
  const shouldShowPreview = useMemo(() => isPreviewOpen, [isPreviewOpen]);

  const previewStyles = useMemo(
    () => ({
      position: 'fixed' as const,
      top: '50%',
      transform: 'translateY(-50%)',
      ...previewCoords,
      border: '1px solid #e5e5e5',
      borderRadius: 8,
      backgroundColor: '#ffffff',
      zIndex: 1000,
      boxShadow: shouldShowPreview ? `rgba(0, 0, 0, 0.1) 0px 4px 12px` : '',
    }),
    [shouldShowPreview, previewCoords],
  );

  const imageDimensions = useMemo(
    () => ({
      height: 16 * magnificationFactor,
      width: 9 * magnificationFactor,
      resizeWidth: 9 * magnificationFactor,
    }),
    [magnificationFactor],
  );

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleDeleteClick = useCallback(() => {
    setIsPreviewOpen(false);
  }, [setIsPreviewOpen]);

  const handleConfirmDelete = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  if (!fileUrl || !isPreviewOpen) return null;

  return (
    <>
      <ImageMaskWrapper onClick={handleClick}>
        <Popconfirm
          title="Are you sure to want to delete this image?"
          onConfirm={handleConfirmDelete}
          okText="Yes, Delete"
          cancelText="No"
        >
          {onDelete && (
            <Button
              type="text"
              size="small"
              onClick={handleDeleteClick}
              icon={<TrashIcon />}
              style={{ color: '#ffffff' }}
            >
              Delete
            </Button>
          )}
        </Popconfirm>
      </ImageMaskWrapper>

      {createPortal(
        <div style={previewStyles}>
          {shouldShowPreview && (
            <Image
              height={imageDimensions.height}
              width={imageDimensions.width}
              src={fileUrl}
              preview={false}
              style={{
                objectFit: 'contain',
              }}
              resizeWidth={imageDimensions.resizeWidth}
              loading="lazy"
            />
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default ImageMask;
